/**
 * Density-aware interference evaluation — separates "needs action" from
 * normal cell-edge overlap in tuned enterprise layouts.
 */

import { channelPoolForBand, channelsInterfere } from './rfChannels'
import { syncApChannels } from './rfRecommendationFixes'
import type { FloorGeometry, PlacedAp } from './types'

export type InterferencePlanStatus = {
  /** Channels assigned and TX trimmed for overlap control. */
  tuned: boolean
  avgTxDbm: number
  /** Rough % of floor expected to show transition/overlap zones for this AP count. */
  expectedHighInterfPct: number
  /** Pairs closer than 14 m on the same channel with TX still high. */
  actionableSameChannelPairs: number
  /** Closest same-channel pair distance (m), if any. */
  closestSameChannelDistM: number | null
}

function distanceM(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const dx = ((x2 - x1) / 100) * floorWidthM
  const dy = ((y2 - y1) / 100) * floorHeightM
  return Math.hypot(dx, dy)
}

/** Expected high-interference % for a given AP density (planning heuristic, not survey). */
export function expectedHighInterferencePct(
  apCount: number,
  floorWidthM: number,
  floorHeightM: number,
  bandGHz: number,
): number {
  if (apCount <= 1) return 0
  const area = Math.max(1, floorWidthM * floorHeightM)
  const conflictR = bandGHz >= 5 ? 22 : 28
  const cellArea = Math.PI * conflictR ** 2
  const pool = channelPoolForBand(bandGHz).length
  const overlapFraction = Math.min(1, (apCount * cellArea * 0.5) / area)
  const reusePressure =
    apCount <= pool ? 0.12 + (apCount / pool) * 0.18 : 0.28 + ((apCount - pool) / apCount) * 0.22
  return Math.min(48, Math.round(overlapFraction * reusePressure * 100))
}

export function evaluateInterferencePlan(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): InterferencePlanStatus {
  const list = syncApChannels(aps.map(a => ({ ...a })), geometry)
  const txValues = list.map(ap => ap.txPowerDbm ?? 15)
  const avgTxDbm = txValues.length ? txValues.reduce((s, t) => s + t, 0) / txValues.length : 15
  const allHaveChannels = list.length >= 2 && list.every(ap => ap.channel != null)
  const noHotTx = list.every(ap => (ap.txPowerDbm ?? 15) <= 14)

  let actionableSameChannelPairs = 0
  let closestSameChannelDistM: number | null = null

  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i]
      const b = list[j]
      if (!channelsInterfere(a.channel, b.channel, geometry.bandGHz)) continue
      const distM = distanceM(a.x, a.y, b.x, b.y, floorWidthM, floorHeightM)
      if (closestSameChannelDistM == null || distM < closestSameChannelDistM) {
        closestSameChannelDistM = distM
      }
      const hot = (a.txPowerDbm ?? 15) >= 15 || (b.txPowerDbm ?? 15) >= 15
      if (distM < 14 && hot) actionableSameChannelPairs += 1
    }
  }

  const tuned = allHaveChannels && noHotTx && avgTxDbm <= 13.5

  return {
    tuned,
    avgTxDbm,
    expectedHighInterfPct: expectedHighInterferencePct(
      list.length,
      floorWidthM,
      floorHeightM,
      geometry.bandGHz,
    ),
    actionableSameChannelPairs,
    closestSameChannelDistM,
  }
}

export type CoChannelWarningLevel = 'none' | 'monitor' | 'action'

/** Whether to surface a co-channel recommendation and at what severity. */
export function coChannelWarningLevel(
  highInterfPct: number,
  severeInterfPct: number,
  plan: InterferencePlanStatus,
): CoChannelWarningLevel {

  if (highInterfPct <= 8) return 'none'

  if (plan.tuned) {
    if (severeInterfPct >= 12 || plan.actionableSameChannelPairs >= 2) return 'action'
    if (highInterfPct <= plan.expectedHighInterfPct + 4) return 'none'
    if (highInterfPct <= plan.expectedHighInterfPct + 12 && plan.actionableSameChannelPairs === 0) {
      return 'monitor'
    }
    if (plan.actionableSameChannelPairs === 0 && highInterfPct < plan.expectedHighInterfPct) return 'none'
  }

  if (severeInterfPct >= 8 || highInterfPct >= 25) return 'action'
  if (highInterfPct >= 12) return 'monitor'
  return 'none'
}

export function coChannelRecommendationCopy(
  highInterfPct: number,
  plan: InterferencePlanStatus,
  level: CoChannelWarningLevel,
): { title: string; detail: string; priority: 'HIGH' | 'MEDIUM' | 'LOW' } {
  const pct = Math.round(highInterfPct)
  const expected = plan.expectedHighInterfPct

  if (level === 'monitor') {
    return {
      priority: 'LOW',
      title: 'Cell overlap within normal range',
      detail: `${pct}% of the floor is in RF transition zones — expected for ${expected}% with this AP density. Channels and TX are tuned; enable band steering in production if clients stick.`,
    }
  }

  if (plan.tuned) {
    return {
      priority: 'MEDIUM',
      title: 'Residual co-channel overlap',
      detail: `${pct}% overlap cells remain after channel + TX tuning (typical ~${expected}% for this layout). Move APs that share a channel closer than 12 m apart, or remove redundant APs in the open workspace.`,
    }
  }

  return {
    priority: 'HIGH',
    title: 'Co-channel overlap',
    detail: `${pct}% of the floor has strong co-channel interference — separate channels and reduce TX power in overlap zones, then re-run Optimize.`,
  }
}
